import { mat4, quat, vec3 } from 'gl-matrix'
import { GLContext } from 'nanogl/types'
import Node from 'nanogl-gltf/lib/elements/Node'
import MaterialOverrideExtension from 'nanogl-gltf/lib/extensions/MaterialOverrideExtension'
import Gltf from 'nanogl-gltf/lib/Gltf'
import { GltfLoaderOptions } from 'nanogl-gltf/lib/io/GltfLoaderOptions'
import MeshRenderer from 'nanogl-gltf/lib/renderer/MeshRenderer'
import GltfTypes from 'nanogl-gltf/lib/types/GltfTypes'
import { Uniform } from 'nanogl-pbr/Input'
import UnlitPass from 'nanogl-pbr/UnlitPass'

import { HexToNewVec3 } from '@/webgl/core/Color'
import { RenderContext } from '@/webgl/core/RenderContext'
import DebugDraw from '@/webgl/dev/debugDraw/DebugDraw'
import { clamp01 } from '@/webgl/math'
import GltfResource from '@/webgl/resources/GltfResource'
import { TimeEntity } from '@/webgl/runner/entities/Time'
import GameWorld from '@/webgl/runner/GameWorld'
import ILoadable from '@/webgl/runner/interfaces/ILoadable'
import { IRenderable } from '@/webgl/runner/interfaces/IRenderable'
import AnimatedSprite, { AnimatedSpriteSettings } from '@/webgl/runner/rendering/AnimatedSprite'
import AntialiasedLine from '@/webgl/runner/rendering/chunks/AntialiasedLine'
import GlowingEye from '@/webgl/runner/rendering/chunks/GlowingEye'
import MeshRendererProxy from '@/webgl/runner/rendering/MeshRendererProxy'
import { backgroundLayer } from '@/webgl/runner/rendering/RenderingUtils'

export type ScrollItemResource = {
  request: string
  id: string
  animation?: AnimatedSpriteSettings
}

export default class BackgroundRenderer implements ILoadable {
  gltf: Gltf
  node: Node = new Node()

  private itemsRenderer: Record<string, IRenderable> = {}
  private scrollResources: ScrollItemResource[] = []

  eye: MeshRenderer
  eyeChunk: GlowingEye

  overrides: MaterialOverrideExtension = new MaterialOverrideExtension()
  options: GltfLoaderOptions = { extensions: [this.overrides] }

  backgroundColor: Uniform
  debug: boolean = true

  public constructor(
    private gl: GLContext,
    private world: GameWorld
  ) {
    this.eyeChunk = new GlowingEye()

    this.backgroundColor = new Uniform('uBaseColor', 3)

    this.overrides.overridePass('', (ctx, m) => {
      const passInstance = m.getPass('color')
      backgroundLayer(this.gl, passInstance.pass as UnlitPass)
      return passInstance.pass
    })

    this.overrides.overridePass('background', (ctx, m) => {
      const passInstance = m.getPass('color')
      const pass = passInstance.pass as UnlitPass
      backgroundLayer(this.gl, pass)
      pass.baseColorFactor.attach(this.backgroundColor)
      return passInstance.pass
    })

    this.overrides.overridePass('glowing_eye', (ctx, m) => {
      const passInstance = m.getPass('color')
      const pass = passInstance.pass as UnlitPass
      backgroundLayer(this.gl, pass)
      pass.inputs.add(this.eyeChunk)
      return passInstance.pass
    })

    this.overrides.overridePass('line', (ctx, m) => {
      const passInstance = m.getPass('color')
      const pass = passInstance.pass as UnlitPass
      pass.glconfig.enableBlend(true)
      pass.glconfig.blendFunc(this.gl.SRC_ALPHA, this.gl.ONE_MINUS_SRC_ALPHA)
      backgroundLayer(this.gl, pass)
      pass.inputs.add(new AntialiasedLine())
      return passInstance.pass
    })

    this.world.root.add(this.node)
  }

  public setResources(resources: ScrollItemResource[]) {
    this.scrollResources = resources
  }

  public setColor(color: number) {
    this.backgroundColor.set(...HexToNewVec3(color))
  }

  public async load(abort: AbortSignal): Promise<void> {
    await Promise.all([this.loadBackground(abort), this.loadScrollResources(abort)])

    // this.glowingEyeNode = this.gltf.getElementByName(GltfTypes.NODE, "glowing_eye").renderable;
    this.eye = this.gltf.getElementByName(GltfTypes.NODE, 'glowing_eye').renderable
    this.gltf.renderables.splice(this.gltf.renderables.indexOf(this.eye), 1)

    this.node.add(this.gltf.root)
  }

  async loadBackground(abort: AbortSignal): Promise<void> {
    this.gltf = await new GltfResource('common/background.glb', this.gl, this.options).load()
  }

  async loadScrollResources(abort: AbortSignal): Promise<void> {
    const base = this.scrollResources.filter((r) => r.animation == undefined)
    const animated = this.scrollResources.filter((r) => r.animation !== undefined)
    const assets = this.world.registry.scrolls

    const loadable: Promise<unknown>[] = []

    for (const resource of animated) {
      const sprite = new AnimatedSprite(resource.request, this.gl, this.options)
      sprite.framerate = resource.animation.framerate || 60
      sprite.loop = resource.animation.loop !== false
      sprite.id = resource.id
      this.itemsRenderer[resource.id] = sprite

      loadable.push(
        sprite.load(abort).then((sprite) => {
          assets.setBounds(sprite.id, sprite.getBounds())
        })
      )
    }

    for (const resource of base) {
      const gltf = new GltfResource(resource.request, this.gl, this.options)
      loadable.push(
        gltf.load(abort).then((gltf) => {
          for (const mesh of gltf.meshes) {
            this.itemsRenderer[mesh.name] = new MeshRendererProxy(gltf, mesh)
            assets.setBounds(mesh.name, this.itemsRenderer[mesh.name].getBounds())
          }
        })
      )
    }

    await Promise.all(loadable)
  }

  public updateEye(renderer: MeshRenderer) {
    this.eye = renderer
    this.node.add(this.eye.node)
  }

  public preRender(time: TimeEntity) {
    const settings = this.world.settings
    const background = this.world.background

    this.eyeChunk.timeUniform.value[0] += time.deltaTime * clamp01(settings.scrollSpeed)

    for (const entity of background.activeItems) {
      this.itemsRenderer[entity.id].preRender(time.deltaTime)

      if (this.itemsRenderer[entity.id] instanceof AnimatedSprite) {
        const sprite: AnimatedSprite = this.itemsRenderer[entity.id] as AnimatedSprite
        sprite.currentTime = entity.lifetime
      }
    }

    this.eye.node.x += (this.world.player.node.x - this.eye.node.x) * time.deltaTime * 1.8
  }

  public render(context: RenderContext) {
    const background = this.world.background

    this.eye.render(context.gl, context.camera, context.mask, context.pass, context.glConfig)
    for (const renderable of this.gltf.renderables) {
      renderable.render(context.gl, context.camera, context.mask, context.pass, context.glConfig)
    }

    for (const item of background.activeItems) {
      this.itemsRenderer[item.id].node._wmatrix.set(item.node._wmatrix)
      this.itemsRenderer[item.id].render(context)
    }

    if (this.debug) {
      for (const item of background.activeItems) {
        const m4 = mat4.create()
        m4.set(item.node._wmatrix)
        DebugDraw.drawGuizmo(m4)

        const bounds = this.itemsRenderer[item.id].getBounds()
        const b1 = vec3.create()
        const b2 = vec3.create()

        vec3.scale(b1, bounds.min, item.node.scale[0])
        vec3.scale(b2, bounds.max, item.node.scale[0])

        vec3.add(b1, b1, item.node._wposition)
        vec3.add(b2, b2, item.node._wposition)
        const m4A = mat4.create()
        const m4B = mat4.create()
        mat4.fromRotationTranslationScale(m4A, quat.create(), b1, vec3.fromValues(0.25, 0.25, 0.25))
        mat4.fromRotationTranslationScale(m4B, quat.create(), b2, vec3.fromValues(0.25, 0.25, 0.25))
        DebugDraw.drawGuizmo(m4A)
        DebugDraw.drawGuizmo(m4B)
      }
    }
  }
}
